import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const { company } = useCompanyInfo();
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.required_err"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        org: form.org.trim(),
        message: form.message.trim(),
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", org: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-3">
          {t("contact.kicker")}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight">
          {t("contact.title")}
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2 space-y-8" data-testid="contact-company-info">
          {[
            { Icon: Mail, label: t("contact.secure_email"), value: company.secure_email },
            { Icon: Phone, label: t("contact.desk"), value: company.phone },
            { Icon: MapPin, label: t("contact.office"), value: company.address },
          ].map(({ Icon, label, value }) => (
            <div key={label} className="flex gap-4">
              <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-1">{label}</div>
                <div className="text-slate-100 whitespace-pre-line">{value}</div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="md:col-span-3 bg-[#0F1C3F]/80 border border-amber-500/25 rounded-2xl p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
                {t("contact.name")} *
              </label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="bg-slate-950/80 border-amber-500/30 text-slate-100 h-11" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
                {t("contact.email")} *
              </label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="bg-slate-950/80 border-amber-500/30 text-slate-100 h-11" />
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
              {t("contact.org")}
            </label>
            <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} data-testid="contact-org" className="bg-slate-950/80 border-amber-500/30 text-slate-100 h-11" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-amber-400/80 mb-2 block">
              {t("contact.message")} *
            </label>
            <Textarea value={form.message} rows={5} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="bg-slate-950/80 border-amber-500/30 text-slate-100" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="contact-submit" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold px-7 h-11 disabled:opacity-60">
            {submitting ? t("contact.sending") : t("contact.send")}
          </Button>
        </form>
      </section>
    </>
  );
}
